"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSlatePresence = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const collaborative_presence_1 = require("@jsonjoy.com/collaborative-presence");
const styles_1 = require("./styles");
const positions_1 = require("../positions");
const json_equal_1 = require("@jsonjoy.com/json-equal");
const isRgaSelection = (sel) => {
    if (!Array.isArray(sel) || sel.length < 8)
        return false;
    const type = sel[5];
    return type === 4 /* JsonCrdtDataType.str */ || type === 5 /* JsonCrdtDataType.bin */ || type === 6 /* JsonCrdtDataType.arr */;
};
const pathsEqual = (a, b) => (0, json_equal_1.deepEqual)(a, b);
/** Lexicographic comparison of two Slate paths: -1 | 0 | 1. */
const comparePaths = (a, b) => {
    const len = Math.min(a.length, b.length);
    for (let i = 0; i < len; i++) {
        const cmp = a[i] - b[i];
        if (cmp !== 0)
            return cmp;
    }
    return a.length - b.length;
};
/** Order two Slate points so that start <= end. */
const orderPoints = (a, b) => {
    const cmp = comparePaths(a.path, b.path);
    if (cmp < 0)
        return [a, b];
    if (cmp > 0)
        return [b, a];
    return a.offset <= b.offset ? [a, b] : [b, a];
};
/**
 * React hook that integrates remote-peer presence rendering into Slate.js.
 *
 * Returns:
 *
 * - `decorate` — a function to pass to `<Editable decorate={decorate}>`.
 * - `sendLocalPresence` — call this after selection/doc changes to broadcast
 *   the local selection to remote peers.
 *
 * The hook subscribes to `manager.onChange` and triggers re-renders when remote
 * presence data arrives. It also sets up a GC timer to remove stale peers.
 *
 * Usage:
 *
 * ```tsx
 * const {decorate, sendLocalPresence} = useSlatePresence({
 *   manager, peritext, editor,
 * });
 * <Editable decorate={decorate} ... />
 * ```
 */
const useSlatePresence = (opts) => {
    const { manager, peritext: peritextRef, editor, userFromMeta, fadeAfterMs = 3000, dimAfterMs = 30000, hideAfterMs = 60000, gcIntervalMs = 5000, } = opts;
    const [, setTick] = React.useState(0);
    React.useEffect(() => {
        if (!manager)
            return;
        const unsubscribe = manager.onChange.listen((_evt) => {
            setTick((t) => t + 1);
        });
        let gcTimer;
        if (gcIntervalMs > 0)
            gcTimer = setInterval(() => manager.removeOutdated(hideAfterMs), gcIntervalMs);
        return () => {
            unsubscribe();
            clearInterval(gcTimer);
        };
    }, [manager, gcIntervalMs, hideAfterMs]);
    // Slate calls `decorate` for every node in the tree. `Text.decorations()`
    // only uses the *offset* from returned ranges — it ignores the path. This
    // means we MUST only return ranges that belong to the text node identified
    // by `path`, and clip cross-node highlight ranges to this node's bounds.
    const decorate = React.useCallback((entry) => {
        if (!manager)
            return [];
        const [node, path] = entry;
        if (typeof node.text !== 'string')
            return [];
        const textLen = node.text.length;
        const api = peritextRef();
        if (!api)
            return [];
        const txt = api.txt;
        const rootBlock = txt.blocks.root;
        const localProcessId = manager.getProcessId();
        const now = Date.now();
        const peers = manager.peers;
        const ranges = [];
        for (const processId in peers) {
            if (processId === localProcessId)
                continue;
            const peerEntry = peers[processId];
            const presence = peerEntry[0];
            const receivedAt = peerEntry[1];
            const age = now - receivedAt;
            if (age >= hideAfterMs)
                continue;
            const selections = presence[collaborative_presence_1.UserPresenceIdx.Selections];
            if (!selections)
                continue;
            const meta = presence[collaborative_presence_1.UserPresenceIdx.Meta];
            const user = userFromMeta ? userFromMeta(meta) : undefined;
            const color = user?.color ?? (0, styles_1.generateColor)(processId);
            const name = user?.name ?? processId.slice(0, 4);
            for (const sel of selections) {
                if (!isRgaSelection(sel))
                    continue;
                let stableSelections;
                try {
                    stableSelections = collaborative_presence_1.peritext.fromDto(txt, sel);
                }
                catch {
                    continue;
                }
                if (!stableSelections.length)
                    continue;
                for (const [range, startIsAnchor] of stableSelections) {
                    const anchorPoint = startIsAnchor ? range.start : range.end;
                    const focusPoint = startIsAnchor ? range.end : range.start;
                    let anchor;
                    let focus;
                    try {
                        anchor = (0, positions_1.pointToSlatePoint)(rootBlock, anchorPoint, editor);
                        focus = (0, positions_1.pointToSlatePoint)(rootBlock, focusPoint, editor);
                    }
                    catch {
                        continue;
                    }
                    if (pathsEqual(focus.path, path)) {
                        const caretInfo = {
                            peerId: processId,
                            color,
                            name,
                            fadeAfterMs,
                            dimAfterMs,
                            hideAfterMs,
                            receivedAt,
                        };
                        ranges.push({
                            anchor: { path, offset: focus.offset },
                            focus: { path, offset: focus.offset },
                            presenceCaret: caretInfo,
                        });
                    }
                    const isCollapsed = pathsEqual(anchor.path, focus.path) && anchor.offset === focus.offset;
                    if (!isCollapsed) {
                        const [start, end] = orderPoints(anchor, focus);
                        // Check if this text node intersects the [start, end] range.
                        const afterEnd = comparePaths(path, end.path) > 0;
                        const beforeStart = comparePaths(path, start.path) < 0;
                        if (!afterEnd && !beforeStart) {
                            const clipStart = pathsEqual(path, start.path) ? start.offset : 0;
                            const clipEnd = pathsEqual(path, end.path) ? end.offset : textLen;
                            if (clipStart < clipEnd) {
                                const highlightColor = (0, styles_1.generateColor)(processId, 0.3);
                                ranges.push({
                                    anchor: { path, offset: clipStart },
                                    focus: { path, offset: clipEnd },
                                    presenceHighlight: highlightColor,
                                });
                            }
                        }
                    }
                }
            }
        }
        return ranges;
    }, [manager, peritextRef, editor, userFromMeta, fadeAfterMs, dimAfterMs, hideAfterMs]);
    const sendLocalPresence = React.useCallback(() => {
        if (!manager)
            return;
        const selection = editor.selection;
        if (!selection)
            return;
        const api = peritextRef();
        if (!api)
            return;
        const txt = api.txt;
        try {
            const p1 = (0, positions_1.slatePointToPoint)(txt, editor, selection.anchor);
            const p2 = (0, positions_1.slatePointToPoint)(txt, editor, selection.focus);
            const range = txt.rangeFromPoints(p1, p2);
            const startIsAnchor = comparePaths(selection.anchor.path, selection.focus.path) < 0 ||
                (comparePaths(selection.anchor.path, selection.focus.path) === 0 &&
                    selection.anchor.offset <= selection.focus.offset);
            const stableSelection = [range, startIsAnchor];
            const dto = collaborative_presence_1.peritext.toDto(txt, [stableSelection]);
            manager.setSelections([dto]);
        }
        catch { }
    }, [editor, peritextRef, manager]);
    return { decorate, sendLocalPresence };
};
exports.useSlatePresence = useSlatePresence;
